/**
 * Driver cost per hour and average speed, as typed (Phase 28 / task 28.5, DEC-331 D-4) —
 * pure, unit-tested.
 *
 * Disposal suggestions price a trip's drive time as miles ÷ average speed × driver cost per
 * hour. The office types dollars ("32.50"); the API stores integer cents, converted with the
 * shared exact parser, never a float multiply. Blank cost means "not set" (suggestions then
 * recommend nothing, D-4). Speed is whole miles per hour.
 */

import { formatCentsAsDollars, parseDollarsToCents } from '@lodex-pro/shared/money';

/** The API's ceilings ($1,000.00 an hour; 90 mph); they only catch a typo. */
export const MAX_DRIVER_CENTS_PER_HOUR = 100_000;
export const MAX_AVG_SPEED_MPH = 90;

export type DriverCostParse =
  | { readonly ok: true; readonly cents: number | null }
  | { readonly ok: false; readonly message: string };

export function parseDriverCost(raw: string): DriverCostParse {
  const p = parseDollarsToCents(raw);
  if (!p.ok) {
    if (p.reason === 'blank') return { ok: true, cents: null };
    return { ok: false, message: 'Enter a dollar amount, like 32.50.' };
  }
  if (p.cents < 0) return { ok: false, message: 'The cost cannot be negative.' };
  if (p.cents > MAX_DRIVER_CENTS_PER_HOUR) {
    return { ok: false, message: 'That is over $1,000 an hour — check the number.' };
  }
  return { ok: true, cents: p.cents };
}

export type SpeedParse =
  | { readonly ok: true; readonly mph: number }
  | { readonly ok: false; readonly message: string };

export function parseAvgSpeed(raw: string): SpeedParse {
  const t = raw.trim();
  if (!/^\d{1,3}$/.test(t)) return { ok: false, message: 'Enter whole miles per hour, like 45.' };
  const mph = Number(t);
  if (mph < 1 || mph > MAX_AVG_SPEED_MPH) {
    return { ok: false, message: `Enter a speed from 1 to ${String(MAX_AVG_SPEED_MPH)} mph.` };
  }
  return { ok: true, mph };
}

/** "32.50" for the input box; '' when not set. */
export function driverCostToInput(cents: number | null): string {
  return cents === null ? '' : formatCentsAsDollars(cents);
}
